import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { CalendarRange, X } from "lucide-react";

export const PRESET_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This Week",
  last_week: "Last Week",
  this_month: "This Month",
  last_month: "Last Month",
  custom: "Custom Range",
};

const fmt = (s) => {
  if (!s) return "";
  const d = new Date(s + "T00:00:00");
  if (isNaN(d)) return s;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

// Build the shareable querystring the dashboard carries into a destination list.
export function buildRangeQS({ period, from, to }, extra = {}) {
  const p = new URLSearchParams();
  if (period) p.set("period", period);
  if (period === "custom") {
    if (from) p.set("start", from);
    if (to) p.set("end", to);
  }
  Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
  const s = p.toString();
  return s ? `?${s}` : "";
}

// Read the active drill-down range from the URL on a destination page.
export function useRangeParams() {
  const [sp] = useSearchParams();
  const period = sp.get("period") || "";
  const start = sp.get("start") || "";
  const end = sp.get("end") || "";
  const status = sp.get("status") || "";
  const unassigned = sp.get("unassigned") || "";
  const active = Boolean(period || status || unassigned);
  let label = "";
  if (period === "custom" && start && end) label = `${fmt(start)} – ${fmt(end)}`;
  else if (period) label = PRESET_LABELS[period] || period;
  else if (status === "outstanding") label = "Outstanding";
  else if (status) label = status.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase());
  const apiQuery = {};
  if (period) apiQuery.period = period;
  if (period === "custom") { if (start) apiQuery.start = start; if (end) apiQuery.end = end; }
  if (status) apiQuery.status = status;
  if (unassigned) apiQuery.unassigned = unassigned;
  return { period, start, end, status, unassigned, active, label, apiQuery };
}

export function apiQueryString(apiQuery, extra = {}) {
  const p = new URLSearchParams();
  Object.entries({ ...apiQuery, ...extra }).forEach(([k, v]) => {
    if (v !== "" && v !== undefined && v !== null) p.set(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

// Reconciliation: the AMOUNT comes from the SAME /dashboard/summary metric the KPI used,
// so banner value == dashboard value by construction. COUNT comes from the destination list.
const METRIC = {
  invoices:      { noun: "invoices",            amount: (s) => s?.invoices?.total },
  collections:   { noun: "payments",            amount: (s) => s?.collections?.current, suffix: " net" },
  membership:    { noun: "memberships",         amount: (s) => s?.membership_revenue?.current },
  bookings:      { noun: "bookings",            amount: (s) => s?.booking_revenue?.current },
  new_customers: { noun: "new customers",       amount: null },
  outstanding:   { noun: "outstanding invoices", amount: (s) => s?.outstanding },
};

// Context banner (with reconciliation running total) shown on a destination list
// when it was opened from a dashboard KPI card.
export function DrillDownBanner({ metric, count }) {
  const { active, label, period, start, end, unassigned, status } = useRangeParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cfg = METRIC[metric] || {};
  // A period-based KPI amount only reconciles when a period (or the period-free Outstanding) is active.
  const expectsAmount = Boolean(cfg.amount) && !unassigned && Boolean(period || status === "outstanding");

  // Same authoritative source as the dashboard KPI (identical date/tenant/branch/RBAC rules).
  const { data: summary, isLoading: sumLoading, isError: sumError } = useQuery({
    queryKey: ["drilldown-summary", period || "today", start, end],
    queryFn: async () => {
      const p = { period: period || "today" };
      if (period === "custom") { p.start = start; p.end = end; }
      return (await api.get(`/dashboard/summary?${new URLSearchParams(p).toString()}`)).data;
    },
    enabled: active && expectsAmount,
    staleTime: 30000,
  });

  if (!active) return null;

  const context = unassigned ? "Unassigned services" : (period ? "From Dashboard" : "Filtered view");
  const amountVal = expectsAmount && summary ? cfg.amount(summary) : null;
  const noun = unassigned ? "unassigned services" : (cfg.noun || metric);

  let primary;
  if (count === undefined || count === null) {
    primary = <span className="text-slate-400">Loading…</span>;
  } else if (count === 0) {
    primary = <span>No {noun}{period ? " for this period" : ""}</span>;
  } else if (expectsAmount && (sumLoading || (amountVal == null && !sumError))) {
    primary = <span>{count} {noun} · <span className="text-slate-400">…</span></span>;
  } else {
    primary = (
      <span>
        {count} {noun}
        {amountVal != null && <> · <span className="tabular">{inr(amountVal)}{cfg.suffix || ""}</span></>}
      </span>
    );
  }

  return (
    <div className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-pawkt-primary/20 bg-pawkt-primary/5 px-4 py-2.5"
         data-testid="drilldown-banner">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
          <CalendarRange className="h-3.5 w-3.5 shrink-0 text-pawkt-primary-deep" />
          <span data-testid="drilldown-context">{context} · {label}</span>
        </div>
        <div className="mt-0.5 text-sm font-extrabold text-slate-900" data-testid="drilldown-total">
          {primary}
        </div>
      </div>
      <button onClick={() => navigate(location.pathname, { replace: true })}
              data-testid="drilldown-clear"
              className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-50">
        <X className="h-3 w-3" /> Clear
      </button>
    </div>
  );
}
